import {Navigate} from "react-router-dom";
import React, {ReactElement, useEffect, useState} from "react";
import {LOGIN_PATH} from "view/routes";
import {onAuthStateChanged} from "api/firebase";
import {Box, CircularProgress} from "@material-ui/core";
import {
  getSessionSwitchRemainingMs,
  isNoticeRedirectInFlight,
  isSessionSwitchInProgress,
} from "tools/auth/sessionSync";

const PrivateRoute = ({children}: {children: ReactElement}): ReactElement => {
  const [state, setState] = useState({authorized: true});

  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let timeoutId: number | null = null;

    const armSwitchTimeout = () => {
      const remainingMs = getSessionSwitchRemainingMs();

      if (!remainingMs) {
        return;
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(() => {
        setState({authorized: false});
        setIsLoading(false);
      }, remainingMs + 100);
    };

    armSwitchTimeout();

    // Cross-tab login/logout/switch-start events are handled at the
    // `Apps` level: when another tab changes session, this tab is
    // hard-navigated to the inert `/session-changed` notice. We do NOT
    // subscribe to those storage events here (a second subscriber just
    // races itself).
    //
    // BUT we still receive a local `onAuthStateChanged(null)` because
    // Firebase's own cross-tab persistence sync fires its listeners on
    // the FIREBASE_KEY storage event right after our notice-redirect
    // event — in the brief window before `window.location.replace`
    // unloads the document. If we naively render `<Navigate to=/login />`
    // here, react-router commits a SPA navigation that flickers the URL
    // and (worse) used to make the originating tab think we beat it to
    // /login. The two guards below keep us in the loader state during
    // that window: `isSessionSwitchInProgress` (originator-side login
    // flow) and `isNoticeRedirectInFlight` (this tab's own pending
    // hard-navigation to /session-changed).
    const unsubscribeAuth = onAuthStateChanged((user) => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (user) {
        setState({authorized: true});
        setIsLoading(false);
        return;
      }

      if (isSessionSwitchInProgress() || isNoticeRedirectInFlight()) {
        setIsLoading(true);
        armSwitchTimeout();
        return;
      }

      if (!user) {
        setState({authorized: false});
      }

      setIsLoading(false);
    });

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      unsubscribeAuth();
    };
  }, []);

  if (isLoading) {
    return (
      <Box>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!state.authorized) {
    return <Navigate to={LOGIN_PATH} />;
  }

  return children;
};

PrivateRoute.defaultProps = {
  exact: false,
};

export default PrivateRoute;
